import { useCoveo } from "components/coveo/Coveo"
import { Icon } from "components/icon/Icon"
import { useNav } from "components/nav-v2/Wrapper"
import { SEARCH_RESULTS_PAGE_SLUG } from "components/search/context"
import { updateSearchList } from "components/search/utils"
import { useConsole } from "contexts/Console"
import { useDictionary } from "contexts/Dictionary"
import { useLocale } from "contexts/Locale"
import { useNavigation } from "contexts/Navigation"
import { breakpoint } from "css/breakpoints"
import { AnimatePresence, useMotionValueEvent } from "framer-motion"
import useKeyboard from "hooks/useKeyboard"
import { useEffect, useLayoutEffect, useRef, useState } from "react"
import RecentlySearched from "./RecentlySearched"
import Suggestions from "./Suggestions"
import { ClearButton, Form, Input, InputContainer, SearchButton } from "./styles"

export default function Search({ onHasValueChange } = {}) {
  // const controller = process.browser ? global.coveoSearchBox : null
  const inputRef = useRef(null)
  const focusRef = useRef(false)
  const dictionary = useDictionary()
  const navigation = useNavigation()
  const locale = useLocale()
  const nav = useNav()
  const keyboardActive = useKeyboard()
  const console = useConsole()
  const coveo = useCoveo()

  const [controller, setController] = useState(null)
  const [state, setState] = useState(null)
  const [queryValue, setQueryValue] = useState("")
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [searchList, setSearchList] = useState(false)
  const [searchFocus, setSearchFocus] = useState(false)

  useLayoutEffect(() => {
    if (!controller) return
    const unsub = controller.subscribe(() => {
      setState(controller.state)
      setQueryValue(controller.state.value)
    })

    return () => unsub()
  }, [controller])

  useLayoutEffect(() => {
    if (localStorage?.getItem("searchList")) {
      setSearchList(JSON.parse(localStorage.getItem("searchList")))
    }
  }, [navigation.slug])

  useLayoutEffect(() => {
    if (global?.location?.search) {
      updateSearchList()
      if (localStorage?.getItem("searchList")) {
        setSearchList(JSON.parse(localStorage.getItem("searchList")))
      }
    }
  }, [global?.location?.search])

  const localeCode = locale?.current?.codes?.www === locale.defaultLocale.codes.www ? "" : `${locale?.current?.codes?.www}/`

  const handleChange = e => {
    console.verbose("coveo:search:change(%o)", { value: e.target.value })
    const value = e.target.value
    if (state?.suggestions?.length > 0) setShowSuggestions(true)
    if (controller) {
      controller.updateText(value)
    } else {
      setQueryValue(value)
    }
  }

  const handleFocus = () => {
    console.verbose("coveo:search:focus(%o)", {})
    coveo.searchBox((err, controller) => {
      if (err) {
        console.log("coveo.searchBox(err: %o)", err)
        console.error(err) //TODO: do something when there is an error
      } else {
        console.verbose("coveo:search:focus:controller(%o)", { controller })
        if (inputRef.current?.value) {
          controller.updateText(inputRef.current.value)
        }
        setController(controller)
        setTimeout(() => {
          setSearchFocus(true)
        }, 300)
      }
    })
  }

  const handleBlur = () => {
    if (keyboardActive) focusRef.current = true
    else focusRef.current = false

    if (!focusRef.current) {
      setTimeout(() => setSearchFocus(false), 100)
    }
  }

  const handleSubmit = e => {
    e.preventDefault()
    const value = queryValue || state?.value
    if (!value) return
    setShowSuggestions(false)
    nav.collapseAll()
    navigation.push(`/${localeCode}${SEARCH_RESULTS_PAGE_SLUG}?q=${value}`)
  }

  const handleSelectSuggestion = value => {
    setShowSuggestions(false)
    setQueryValue(value)
    controller?.updateText(value)
    nav.collapseAll()
    navigation.push(`/${localeCode}${SEARCH_RESULTS_PAGE_SLUG}?q=${value}`)
  }

  const handleSelectRecent = value => {
    setQueryValue(value)
    controller?.updateText(value)
    nav.collapseAll()
    navigation.push(`/${localeCode}${SEARCH_RESULTS_PAGE_SLUG}?q=${value}`)
  }

  const handleClearInput = () => {
    setShowSuggestions(false)
    setQueryValue("")
    controller?.clear()
    if (keyboardActive) inputRef.current?.focus()
  }

  const handleClearHistory = () => {
    localStorage?.removeItem("searchList")
    setTimeout(() => setSearchList(false), 100)
  }

  const hasValue = !!(queryValue || state?.value)
  const showRecentlySearched = searchList && !hasValue && searchFocus
  const showSuggestionsList = showSuggestions && state?.suggestions?.length > 0 && queryValue

  useEffect(() => {
    onHasValueChange?.(hasValue)
  }, [hasValue, onHasValueChange])

  useMotionValueEvent(nav.main.panes.secondary.motion.expanded, "change", latest => {
    if (latest === 1) {
      inputRef.current?.focus()
    }
    if (latest === 0) {
      setQueryValue("")
      setShowSuggestions(false)
      setSearchFocus(false)
      controller?.clear()
    }
  })

  useMotionValueEvent(nav.main.panes.secondary.motion.expanded, "change", latest => {
    if (latest === 1) {
      const mql = global.matchMedia(`(min-width: ${breakpoint.m})`)
      if (mql.matches) {
        inputRef.current?.focus()
      }
    }
  })

  return (
    <Form onSubmit={handleSubmit}>
      <InputContainer>
        <SearchButton type='submit' aria-label={dictionary.search()}>
          <Icon type='search' />
        </SearchButton>
        <Input
          ref={inputRef}
          className={keyboardActive ? "keyboard-focus" : undefined}
          type='search'
          placeholder={dictionary.search()}
          value={queryValue}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={e => e.key === "Enter" && handleSubmit(e)}
          autoCapitalize='off'
          autoComplete='off'
          autoCorrect='off'
          spellCheck='false'
          maxLength='1010'
          enterKeyHint='search'
          aria-label={dictionary.searchAria()}
          suppressHydrationWarning
        />
        {hasValue && (
          <ClearButton type='button' aria-label={dictionary.clearSearch()} onClick={handleClearInput}>
            <Icon type='close' />
          </ClearButton>
        )}
      </InputContainer>

      <AnimatePresence>
        {showRecentlySearched && <RecentlySearched searchList={searchList} onSelect={handleSelectRecent} onClear={handleClearHistory} />}
        {showSuggestionsList && <Suggestions suggestions={state.suggestions} onSelect={handleSelectSuggestion} />}
      </AnimatePresence>
    </Form>
  )
}
